import {
  useCallback,
  useEffect,
  useState,
} from "react";

import NivelService from "../../../../../../services/LevelService";
import GradoService from "../../../../../../services/GradesService";
import CompetenciaService from "../../../../../../services/CompetenciaService";
import UnidadService from "../../../../../../services/UnidadService";
import { useAuth } from "../../../../../../context/AuthContext";
import documentAnalyzerService from "../../../../../../services/documentAnalyzerService";

import {
  cache,
  LS_KEY,
} from "./sessionInputStep.cache";

const useSessionInputStep = ({
  onNext,
  setInputsGenerados,
}) => {
  const { user } = useAuth();

  const rolUsuario =
    user?.rol?.nombre?.toUpperCase();

  const institucionIdActiva =
    user?.institucion?.id ?? null;

  // ============================================================
  // CATÁLOGOS
  // ============================================================

  const [niveles, setNiveles] = useState([]);
  const [areas, setAreas] = useState([]);
  const [, setCompetencias] =
    useState([]);
  const [grados, setGrados] = useState([]);
  const [unidades, setUnidades] = useState([]);

  // ============================================================
  // ESTADOS DE CARGA
  // ============================================================

  const [isInitializing, setIsInitializing] =
    useState(true);

  const [loadingUnidades, setLoadingUnidades] =
    useState(false);

  const [loadingCursos, setLoadingCursos] =
    useState(false);

  const [
    ,
    setLoadingCompetencias,
  ] = useState(false);

  const [loadingGrados, setLoadingGrados] =
    useState(false);

  // ============================================================
  // SELECCIONES CURRICULARES
  // ============================================================

  const [
    nivelSeleccionado,
    setNivelSeleccionado,
  ] = useState("");

  const [
    areaSeleccionada,
    setAreaSeleccionada,
  ] = useState("");

  const [
    competenciaSeleccionada,
    setCompetenciaSeleccionada,
  ] = useState("");

  const [
    capacidadesSeleccionadas,
    setCapacidadesSeleccionadas,
  ] = useState([]);

  const [
    gradoSeleccionado,
    setGradoSeleccionado,
  ] = useState("");

  const [
    desempenosSeleccionados,
    setDesempenosSeleccionados,
  ] = useState([]);

  const [
    unidadSeleccionada,
    setUnidadSeleccionada,
  ] = useState("");

  const [
    cantidadSesiones,
    setCantidadSesiones,
  ] = useState("");

  const [institucionId, setInstitucionId] =
    useState(null);

  // ============================================================
  // DATOS OBTENIDOS DEL DOCUMENTO
  // ============================================================

  /*
   * archivoCargado indica que existe un documento analizado
   * o en proceso de análisis asociado al flujo.
   *
   * No implica que todavía exista una instancia File:
   * al regresar a esta vista puede restaurarse únicamente
   * su representación persistida.
   *
   * Cambiar selectores manualmente no debe modificarlo.
   */
  const [archivoCargado, setArchivoCargado] =
    useState(false);

  const [
    fileIdDocumento,
    setFileIdDocumento,
  ] = useState(null);

  const [
    infoArchivoDocumento,
    setInfoArchivoDocumento,
  ] = useState(null);

  const [
    resultadoDocumentoPendiente,
    setResultadoDocumentoPendiente,
  ] = useState(null);

  const [tituloSesion, setTituloSesion] =
    useState("");

  const [propositoSesion, setPropositoSesion] =
    useState("");

  // ============================================================
  // UTILIDADES
  // ============================================================

  const normalizarTexto = useCallback((texto) => {
    if (!texto) {
      return "";
    }

    return String(texto)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  const obtenerNombreCompetencia = useCallback(
    (competencia) =>
      competencia?.nombre ??
      competencia?.competencia ??
      "",
    []
  );

  const normalizarLista = useCallback(
    (valor) =>
      Array.isArray(valor) ? valor : [],
    []
  );

  // ============================================================
  // SERVICIOS CON CACHÉ
  // ============================================================

  const getAreas = useCallback(
    async (nivelId) => {
      if (!nivelId) {
        return [];
      }

      if (cache.cursosPorNivel[nivelId]) {
        return cache.cursosPorNivel[nivelId];
      }

      const response =
        await GradoService.listarAreasPorNivel(
          nivelId
        );

      const data = Array.isArray(response?.data)
        ? response.data
        : [];

      cache.cursosPorNivel[nivelId] = data;

      return data;
    },
    []
  );

  const getCompetencias = useCallback(
    async (nivelId, areaId) => {
      if (!nivelId || !areaId) {
        return [];
      }

      const key = `${nivelId}-${areaId}`;

      if (cache.competenciasPorCurso[key]) {
        return cache.competenciasPorCurso[key];
      }

      const response =
        await CompetenciaService.obtenerCompetencias(
          nivelId,
          areaId
        );

      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];

      cache.competenciasPorCurso[key] = data;

      return data;
    },
    []
  );

  const getCapacidades = useCallback(
    async (
      nivelId,
      areaId,
      competenciaId
    ) => {
      if (
        !nivelId ||
        !areaId ||
        !competenciaId
      ) {
        return [];
      }

      const key =
        `${nivelId}-${areaId}-${competenciaId}`;

      if (
        cache.capacidadesPorCompetencia[key]
      ) {
        return cache
          .capacidadesPorCompetencia[key];
      }

      const response =
        await CompetenciaService.obtenerCapacidades(
          nivelId,
          areaId,
          competenciaId
        );

      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];

      cache.capacidadesPorCompetencia[key] =
        data;

      return data;
    },
    []
  );

  const getGrados = useCallback(
    async (nivelId) => {
      if (!nivelId) {
        return [];
      }

      if (cache.gradosPorNivel[nivelId]) {
        return cache.gradosPorNivel[nivelId];
      }

      const response =
        await NivelService.listarGradosPorNivel(
          nivelId
        );

      const data = Array.isArray(response?.data)
        ? response.data
        : [];

      cache.gradosPorNivel[nivelId] = data;

      return data;
    },
    []
  );

  const getDesempenos = useCallback(
    async (
      nivelId,
      areaId,
      competenciaId,
      gradoId
    ) => {
      if (
        !nivelId ||
        !areaId ||
        !competenciaId ||
        !gradoId
      ) {
        return [];
      }

      const key =
        `${nivelId}-${areaId}-` +
        `${competenciaId}-${gradoId}`;

      if (cache.desempenosPorClave[key]) {
        return cache.desempenosPorClave[key];
      }

      const response =
        await CompetenciaService.obtenerDesempeños(
          nivelId,
          areaId,
          competenciaId,
          gradoId
        );

      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];

      cache.desempenosPorClave[key] = data;

      return data;
    },
    []
  );

  const getUnidades = useCallback(
    async (instId) => {
      if (!instId) {
        return [];
      }

      try {
        const response =
          await UnidadService.obtenerUnidades({
            idInstitucionEdu: instId,
          });

        return Array.isArray(response?.items)
          ? response.items
          : [];
      } catch (error) {
        console.error(
          "Error obteniendo unidades:",
          error
        );

        return [];
      }
    },
    []
  );

  // ============================================================
  // LIMPIEZA DE SELECCIONES
  // ============================================================

  const limpiarDesdeNivel = useCallback(() => {
    setAreaSeleccionada("");
    setCompetenciaSeleccionada("");
    setCapacidadesSeleccionadas([]);
    setGradoSeleccionado("");
    setDesempenosSeleccionados([]);
    setUnidadSeleccionada("");

    setAreas([]);
    setCompetencias([]);
    setGrados([]);
    setUnidades([]);
  }, []);

  const limpiarDesdeArea = useCallback(() => {
    setCompetenciaSeleccionada("");
    setCapacidadesSeleccionadas([]);
    setGradoSeleccionado("");
    setDesempenosSeleccionados([]);
    setUnidadSeleccionada("");

    setCompetencias([]);
    setUnidades([]);
  }, []);

  // ============================================================
  // RESTAURAR ESTADO
  // ============================================================

  const restore = useCallback(
    async (data, instId) => {
      if (!data) {
        return;
      }

      const {
        nivel,
        area,
        grado,
        unidad,
        sesiones,

        tituloSesion:
          tituloGuardado,

        propositoSesion:
          propositoGuardado,

        archivoCargado:
          archivoCargadoGuardado,

        fileIdDocumento:
          fileIdDocumentoGuardado,

        infoArchivoDocumento:
          infoArchivoDocumentoGuardado,
      } = data;

      try {
        if (tituloGuardado) {
          setTituloSesion(tituloGuardado);
        }

        if (propositoGuardado) {
          setPropositoSesion(
            propositoGuardado
          );
        }

        const documentoPersistidoValido =
          Boolean(
            archivoCargadoGuardado &&
            fileIdDocumentoGuardado &&
            infoArchivoDocumentoGuardado
          );

        setArchivoCargado(
          documentoPersistidoValido
        );

        setFileIdDocumento(
          documentoPersistidoValido
            ? fileIdDocumentoGuardado
            : null
        );

        setInfoArchivoDocumento(
          documentoPersistidoValido
            ? infoArchivoDocumentoGuardado
            : null
        );

        if (!nivel) {
          return;
        }

        const nivelId = Number(nivel);

        setNivelSeleccionado(nivelId);

        const [
          resultadoAreas,
          resultadoGrados,
        ] = await Promise.allSettled([
          getAreas(nivelId),
          getGrados(nivelId),
        ]);

        const areasNivel =
          resultadoAreas.status === "fulfilled"
            ? resultadoAreas.value
            : [];

        const gradosNivel =
          resultadoGrados.status === "fulfilled"
            ? resultadoGrados.value
            : [];

        setAreas(areasNivel);
        setGrados(gradosNivel);

        if (!area) {
          if (sesiones) {
            setCantidadSesiones(
              Number(sesiones)
            );
          }

          return;
        }

        const areaId = Number(area);

        setAreaSeleccionada(areaId);

        if (!grado) {
          if (sesiones) {
            setCantidadSesiones(
              Number(sesiones)
            );
          }

          return;
        }

        const gradoId = Number(grado);

        setGradoSeleccionado(gradoId);
        setLoadingUnidades(true);

        const resultadoUnidades =
          await getUnidades(instId);

        const unidadesBD =
          Array.isArray(resultadoUnidades)
            ? resultadoUnidades
            : [];

        setUnidades(unidadesBD);

        const unidadValida =
          unidadesBD.some(
            (item) =>
              String(item.id) ===
              String(unidad)
          );

        setUnidadSeleccionada(
          unidadValida
            ? Number(unidad)
            : ""
        );

        if (sesiones) {
          setCantidadSesiones(
            Number(sesiones)
          );
        }
      } catch (error) {
        console.error(
          "Error restaurando formulario:",
          error
        );
      } finally {
        setLoadingUnidades(false);
      }
    },
    [
      getAreas,
      getGrados,
      getUnidades,
    ]
  );

  // ============================================================
  // ARCHIVO
  // ============================================================

  const handleArchivoSeleccionado =
    useCallback((archivo) => {
      setArchivoCargado(true);
      setCantidadSesiones(1);

      /*
      * Al seleccionar otro archivo, el file_id anterior
      * deja de ser válido para este flujo.
      */
      setFileIdDocumento(null);

      setInfoArchivoDocumento(
        documentAnalyzerService
          .obtenerInfoArchivo(archivo)
      );

      setResultadoDocumentoPendiente(null);

      setTituloSesion("");
      setPropositoSesion("");
    }, []);

  const handleDocumentoAnalizado =
    useCallback((resultado) => {
      if (!resultado) {
        return;
      }

      const fileId =
        typeof resultado.file_id === "string"
          ? resultado.file_id.trim() || null
          : null;

      if (!fileId) {
        console.error(
          "El análisis terminó sin devolver file_id."
        );

        setArchivoCargado(false);
        setFileIdDocumento(null);
        setInfoArchivoDocumento(null);
        return;
      }

      setArchivoCargado(true);
      setCantidadSesiones(1);

      setFileIdDocumento(
        fileId
      );

      setInfoArchivoDocumento(
        (infoAnterior) =>
          documentAnalyzerService
            .obtenerInfoArchivoPersistido({
              archivo:
                resultado.archivo,

              cantidadPaginas:
                resultado.cantidad_paginas,

              infoOriginal:
                infoAnterior,
            })
      );

      setTituloSesion(
        resultado.titulo_sesion ?? ""
      );

      setPropositoSesion(
        resultado.proposito_sesion ?? ""
      );

      setResultadoDocumentoPendiente(
        resultado
      );
    }, []);

  const handleArchivoEliminado =
    useCallback(() => {
      setArchivoCargado(false);
      setFileIdDocumento(null);
      setInfoArchivoDocumento(null);

      setResultadoDocumentoPendiente(null);

      setNivelSeleccionado("");
      setAreaSeleccionada("");
      setCompetenciaSeleccionada("");
      setCapacidadesSeleccionadas([]);
      setGradoSeleccionado("");
      setDesempenosSeleccionados([]);
      setUnidadSeleccionada("");
      setCantidadSesiones("");

      setTituloSesion("");
      setPropositoSesion("");

      setAreas([]);
      setCompetencias([]);
      setGrados([]);
      setUnidades([]);

      setLoadingCursos(false);
      setLoadingCompetencias(false);
      setLoadingGrados(false);
      setLoadingUnidades(false);

      localStorage.removeItem(
        LS_KEY
      );
    }, []);

  // ============================================================
  // APLICAR RESPUESTA DE IA
  // ============================================================

  useEffect(() => {
    if (
      !resultadoDocumentoPendiente ||
      !niveles.length
    ) {
      return undefined;
    }

    let cancelado = false;

    const aplicarDocumento = async () => {
      const resultado =
        resultadoDocumentoPendiente;

      setLoadingCursos(true);
      setLoadingCompetencias(true);
      setLoadingGrados(true);
      setLoadingUnidades(true);

      try {
        setNivelSeleccionado("");
        setAreaSeleccionada("");
        setCompetenciaSeleccionada("");
        setCapacidadesSeleccionadas([]);
        setGradoSeleccionado("");
        setDesempenosSeleccionados([]);
        setUnidadSeleccionada("");

        setAreas([]);
        setCompetencias([]);
        setGrados([]);
        setUnidades([]);

        setCantidadSesiones(1);

        setTituloSesion(
          resultado.titulo_sesion ?? ""
        );

        setPropositoSesion(
          resultado.proposito_sesion ?? ""
        );

        // ====================================================
        // NIVEL
        // ====================================================

        const nivelPorId =
          resultado?.nivel?.id != null
            ? niveles.find(
                (item) =>
                  Number(item.id) ===
                  Number(resultado.nivel.id)
              )
            : null;

        const nivelPorNombre =
          niveles.find(
            (item) =>
              normalizarTexto(item.nombre) ===
              normalizarTexto(
                resultado?.nivel?.nombre
              )
          );

        const nivelEncontrado =
          nivelPorId ?? nivelPorNombre;

        if (!nivelEncontrado || cancelado) {
          return;
        }

        const nivelId = Number(
          nivelEncontrado.id
        );

        setNivelSeleccionado(nivelId);

        const [
          resultadoAreas,
          resultadoGrados,
        ] = await Promise.allSettled([
          getAreas(nivelId),
          getGrados(nivelId),
        ]);

        if (cancelado) {
          return;
        }

        const areasNivel =
          resultadoAreas.status === "fulfilled"
            ? normalizarLista(
                resultadoAreas.value
              )
            : [];

        const gradosNivel =
          resultadoGrados.status === "fulfilled"
            ? normalizarLista(
                resultadoGrados.value
              )
            : [];

        setAreas(areasNivel);
        setGrados(gradosNivel);

        // ====================================================
        // ÁREA
        // ====================================================

        const areaPorId =
          resultado?.area?.id != null
            ? areasNivel.find(
                (item) =>
                  Number(item.id) ===
                  Number(resultado.area.id)
              )
            : null;

        const areaPorNombre =
          areasNivel.find(
            (item) =>
              normalizarTexto(item.nombre) ===
              normalizarTexto(
                resultado?.area?.nombre
              )
          );

        const areaEncontrada =
          areaPorId ?? areaPorNombre;

        /*
         * Si no coincide el área, sus opciones permanecen
         * cargadas para que el usuario seleccione manualmente.
         */
        if (!areaEncontrada || cancelado) {
          return;
        }

        const areaId = Number(
          areaEncontrada.id
        );

        setAreaSeleccionada(areaId);

        let competenciasArea = [];

        try {
          competenciasArea =
            await getCompetencias(
              nivelId,
              areaId
            );
        } catch (error) {
          console.error(
            "Error cargando competencias:",
            error
          );
        }

        if (cancelado) {
          return;
        }

        setCompetencias(
          normalizarLista(
            competenciasArea
          )
        );

        // ====================================================
        // COMPETENCIA
        // ====================================================

        const competenciaPorId =
          resultado?.competencia?.id != null
            ? competenciasArea.find(
                (item) =>
                  Number(item.id) ===
                  Number(
                    resultado.competencia.id
                  )
              )
            : null;

        const competenciaPorNombre =
          competenciasArea.find(
            (item) =>
              normalizarTexto(
                obtenerNombreCompetencia(item)
              ) ===
              normalizarTexto(
                resultado?.competencia?.nombre
              )
          );

        const competenciaEncontrada =
          competenciaPorId ??
          competenciaPorNombre;

        if (cancelado) {
          return;
        }

        /*
         * La competencia es opcional en el analisis documental.
         * Solo se conserva cuando coincide con el catalogo real
         * del nivel y area seleccionados. Si no hubo deteccion, el
         * flujo continua con grado y unidad, dejando la competencia
         * vacia para la siguiente vista.
         */
        const competenciaId =
          competenciaEncontrada
            ? Number(
                competenciaEncontrada.id
              )
            : null;

        if (competenciaId) {
          setCompetenciaSeleccionada(
            competenciaId
          );

          const resultadoCapacidades =
            await Promise.allSettled([
              getCapacidades(
                nivelId,
                areaId,
                competenciaId
              ),
            ]);

          if (
            !cancelado &&
            resultadoCapacidades[0].status ===
              "fulfilled"
          ) {
            setCapacidadesSeleccionadas(
              normalizarLista(
                resultadoCapacidades[0].value
              ).map((item) => item.id)
            );
          }
        } else {
          setCompetenciaSeleccionada("");
          setCapacidadesSeleccionadas([]);
          setDesempenosSeleccionados([]);
        }

        // ====================================================
        // GRADO
        // ====================================================

        const gradoPorId =
          resultado?.grado?.id != null
            ? gradosNivel.find(
                (item) =>
                  Number(item.id) ===
                  Number(resultado.grado.id)
              )
            : null;

        const gradoPorNumero =
          gradosNivel.find(
            (item) =>
              Number(item.descripcion) ===
              Number(
                resultado?.grado?.numero
              )
          );

        const gradoEncontrado =
          gradoPorId ?? gradoPorNumero;

        if (!gradoEncontrado || cancelado) {
          return;
        }

        const gradoId = Number(
          gradoEncontrado.id
        );

        setGradoSeleccionado(gradoId);

        // ====================================================
        // DESEMPEÑOS Y UNIDADES
        // ====================================================

        const [
          resultadoDesempenos,
          resultadoUnidades,
        ] = await Promise.allSettled([
          competenciaId
            ? getDesempenos(
                nivelId,
                areaId,
                competenciaId,
                gradoId
              )
            : Promise.resolve([]),
          getUnidades(institucionId),
        ]);

        if (cancelado) {
          return;
        }

        const desempenosBD =
          resultadoDesempenos.status ===
          "fulfilled"
            ? normalizarLista(
                resultadoDesempenos.value
              )
            : [];

        const unidadesBD =
          resultadoUnidades.status ===
          "fulfilled"
            ? normalizarLista(
                resultadoUnidades.value
              )
            : [];

        setDesempenosSeleccionados(
          desempenosBD.map(
            (item) => item.id
          )
        );

        setUnidades(unidadesBD);

        /*
         * La unidad ya no la selecciona la IA.
         * El usuario deberá elegirla manualmente
         * entre las unidades de la institución.
         */
        setUnidadSeleccionada("");
      } catch (error) {
        console.error(
          "Error aplicando datos del documento:",
          error
        );
      } finally {
        if (!cancelado) {
          setLoadingCursos(false);
          setLoadingCompetencias(false);
          setLoadingGrados(false);
          setLoadingUnidades(false);

          setResultadoDocumentoPendiente(
            null
          );
        }
      }
    };

    aplicarDocumento();

    return () => {
      cancelado = true;
    };
  }, [
    resultadoDocumentoPendiente,
    niveles,
    institucionId,
    normalizarTexto,
    normalizarLista,
    obtenerNombreCompetencia,
    getAreas,
    getCompetencias,
    getCapacidades,
    getGrados,
    getDesempenos,
    getUnidades,
  ]);

  // ============================================================
  // CAMBIO MANUAL DE NIVEL
  // ============================================================

  const handleNivelChange = async (event) => {
    const nivelId = Number(
      event.target.value
    );

    setResultadoDocumentoPendiente(null);
    setNivelSeleccionado(nivelId);

    limpiarDesdeNivel();

    setCantidadSesiones(
      archivoCargado ? 1 : ""
    );

    setLoadingCursos(true);

    try {
      const [
        resultadoAreas,
        resultadoGrados,
      ] = await Promise.allSettled([
        getAreas(nivelId),
        getGrados(nivelId),
      ]);

      setAreas(
        resultadoAreas.status === "fulfilled"
          ? resultadoAreas.value
          : []
      );

      setGrados(
        resultadoGrados.status === "fulfilled"
          ? resultadoGrados.value
          : []
      );
    } catch (error) {
      console.error(
        "Error cambiando nivel:",
        error
      );
    } finally {
      setLoadingCursos(false);
    }
  };

  // ============================================================
  // CAMBIO MANUAL DE ÁREA
  // ============================================================

  const handleAreaChange = async (event) => {
    const areaId = Number(
      event.target.value
    );

    setResultadoDocumentoPendiente(null);
    setAreaSeleccionada(areaId);

    limpiarDesdeArea();

    setCantidadSesiones(
      archivoCargado ? 1 : ""
    );

    setLoadingGrados(true);

    try {
      const gradosNivel = await getGrados(
        Number(nivelSeleccionado)
      );

      setGrados(gradosNivel);
    } catch (error) {
      console.error(
        "Error cambiando área:",
        error
      );
    } finally {
      setLoadingGrados(false);
    }
  };

  // ============================================================
  // CAMBIO MANUAL DE GRADO
  // ============================================================

  const handleGradoChange = async (event) => {
    const gradoId = Number(
      event.target.value
    );

    setResultadoDocumentoPendiente(null);
    setGradoSeleccionado(gradoId);

    setDesempenosSeleccionados([]);
    setUnidadSeleccionada("");
    setUnidades([]);

    setCantidadSesiones(
      archivoCargado ? 1 : ""
    );

    setLoadingUnidades(true);

    try {
      const unidadesInstitucion =
        await getUnidades(institucionId);

      setUnidades(unidadesInstitucion);
    } catch (error) {
      console.error(
        "Error cambiando grado:",
        error
      );
    } finally {
      setLoadingUnidades(false);
    }
  };

  // ============================================================
  // CAMBIO MANUAL DE UNIDAD
  // ============================================================

  const handleUnidadChange = useCallback(
    (valor) => {
      setUnidadSeleccionada(valor);

      if (archivoCargado) {
        setCantidadSesiones(1);
      }
    },
    [archivoCargado]
  );

  // ============================================================
  // CAMBIO DE CANTIDAD DE SESIONES
  // ============================================================

  const handleCantidadSesionesChange =
    useCallback(
      (valor) => {
        if (archivoCargado) {
          setCantidadSesiones(1);
          return;
        }

        setCantidadSesiones(
          Number(valor)
        );
      },
      [archivoCargado]
    );

  // ============================================================
  // CONTINUAR
  // ============================================================

  // const handleContinuar = () => {
  //   const unidadObj = unidades.find(
  //     (item) =>
  //       String(item.id) ===
  //       String(unidadSeleccionada)
  //   );

  //   const filas = Array.from(
  //     {
  //       length: Number(cantidadSesiones),
  //     },
  //     (_, index) => ({
  //       nivel: nivelSeleccionado,
  //       area: areaSeleccionada,
  //       competencia:
  //         competenciaSeleccionada,
  //       capacidades:
  //         capacidadesSeleccionadas,
  //       grado: gradoSeleccionado,
  //       desempenos:
  //         desempenosSeleccionados,

  //       unidadId: unidadObj?.id,
  //       unidad: unidadObj?.nombreUnidad,
  //       unidadNombre:
  //         unidadObj?.nombreUnidad,
  //       nroUnidad: unidadObj?.nroUnidad,
  //       situacion:
  //         unidadObj?.situacionSignificativa,

  //       nroSesion: index + 1,
  //       institucionId,

  //       /*
  //        * Estos datos se transportan hacia los
  //        * pasos posteriores, aunque todavía no
  //        * se muestren en esta vista.
  //        */
  //       tituloSesion:
  //         archivoCargado
  //           ? tituloSesion
  //           : "",

  //       propositoSesion:
  //         archivoCargado
  //           ? propositoSesion
  //           : "",

  //       origenDocumento: archivoCargado,
  //     })
  //   );

  //   setInputsGenerados(filas);
  //   onNext();
  // };

  // ============================================================
  // CONTINUAR
  // ============================================================

  const handleContinuar = () => {
    const unidadObj = unidades.find(
      (item) =>
        String(item.id) ===
        String(unidadSeleccionada)
    );

    /*
    * Cuando existe un archivo cargado y analizado:
    *
    * tituloSesion    -> tema
    * propositoSesion -> descripcion
    *
    * Estos son los campos que consume el siguiente paso.
    */
    const temaDocumento = archivoCargado
      ? String(tituloSesion || "").trim()
      : "";

    const descripcionDocumento = archivoCargado
      ? String(propositoSesion || "").trim()
      : "";

    const filas = Array.from(
      {
        length: Number(cantidadSesiones),
      },
      (_, index) => ({
        // ======================================================
        // DATOS CURRICULARES
        // ======================================================

        nivel: nivelSeleccionado,
        area: areaSeleccionada,
        competencia:
          archivoCargado
            ? competenciaSeleccionada || null
            : null,
        competenciaNombre: "",
        competenciaOrigen:
          archivoCargado && competenciaSeleccionada
            ? "IA_DOCUMENTO"
            : null,
        capacidades:
          archivoCargado
            ? capacidadesSeleccionadas
            : [],
        grado: gradoSeleccionado,
        desempenos:
          archivoCargado
            ? desempenosSeleccionados
            : [],

        // ======================================================
        // DATOS DE LA UNIDAD
        // ======================================================

        unidadId: unidadObj?.id,
        unidad: unidadObj?.nombreUnidad,
        unidadNombre: unidadObj?.nombreUnidad,
        nroUnidad: unidadObj?.nroUnidad,
        situacion: unidadObj?.situacionSignificativa,

        // ======================================================
        // DATOS GENERALES
        // ======================================================

        nroSesion: index + 1,
        institucionId,

        // ======================================================
        // TÍTULO Y PROPÓSITO PARA EL SIGUIENTE PASO
        // ======================================================

        /*
        * SessionSequenceTable y useSessionSequenceStep
        * consumen directamente estas propiedades.
        *
        * Cuando existe documento, llegan completas y el
        * siguiente paso no debe llamar a generarTemas().
        *
        * Cuando no existe documento, permanecen vacías y
        * se conserva la generación habitual mediante IA.
        */
        tema: temaDocumento,
        descripcion: descripcionDocumento,

        /*
        * Se conservan también los nombres originales
        * utilizados por el analizador documental.
        */
        tituloSesion: temaDocumento,
        propositoSesion: descripcionDocumento,

        /*
        * Indicador adicional de trazabilidad.
        */
        origenDocumento: Boolean(archivoCargado),

        /*
         * El identificador se conserva en la segunda vista
         * y se transforma en file_id al construir el payload
         * de la tercera vista.
         */
        fileId:
          archivoCargado
            ? fileIdDocumento
            : null,

        /*
         * Metadatos usados únicamente para restaurar la
         * representación visual del documento.
         */
        archivoDocumento:
          archivoCargado
            ? infoArchivoDocumento
            : null,
      })
    );

    setInputsGenerados(filas);
    onNext();
  };

  // ============================================================
  // INICIALIZACIÓN
  // ============================================================

  useEffect(() => {
    const init = async () => {
      setIsInitializing(true);

      if (!institucionIdActiva) {
        setIsInitializing(false);
        return;
      }

      try {
        const responseNiveles =
          await NivelService.listarNiveles();

        const nivelesBD = Array.isArray(
          responseNiveles?.data
        )
          ? responseNiveles.data
          : [];

        setInstitucionId(
          institucionIdActiva
        );

        setNiveles(nivelesBD);

        const saved =
          localStorage.getItem(LS_KEY);

        if (saved) {
          try {
            const data = JSON.parse(saved);

            await restore(
              data,
              institucionIdActiva
            );
          } catch (error) {
            console.error(
              "No se pudo restaurar el formulario:",
              error
            );

            localStorage.removeItem(
              LS_KEY
            );
          }
        }
      } catch (error) {
        console.error(
          "Error inicializando formulario:",
          error
        );
      } finally {
        setIsInitializing(false);
      }
    };

    init();
  }, [
    institucionIdActiva,
    restore,
  ]);

  // ============================================================
  // LOCAL STORAGE
  // ============================================================

  useEffect(() => {
    if (isInitializing) {
      return;
    }

    /*
     * Se permite persistir el documento incluso cuando
     * todavía no se ha podido seleccionar un nivel.
     */
    if (
      !nivelSeleccionado &&
      !archivoCargado
    ) {
      return;
    }

    localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        nivel: nivelSeleccionado,
        area: areaSeleccionada,
        grado: gradoSeleccionado,
        unidad: unidadSeleccionada,
        sesiones: cantidadSesiones,

        tituloSesion,
        propositoSesion,

        archivoCargado,
        fileIdDocumento,
        infoArchivoDocumento,
      })
    );
  }, [
    archivoCargado,
    areaSeleccionada,
    cantidadSesiones,
    fileIdDocumento,
    gradoSeleccionado,
    infoArchivoDocumento,
    isInitializing,
    nivelSeleccionado,
    propositoSesion,
    tituloSesion,
    unidadSeleccionada,
  ]);

  // ============================================================
  // RESPUESTA DEL HOOK
  // ============================================================

  return {
    canGoBackToEntry:
      rolUsuario === "ADMIN" ||
      rolUsuario === "DIRECTOR",

    formularioCompleto: Boolean(
      nivelSeleccionado &&
        areaSeleccionada &&
        gradoSeleccionado &&
        unidadSeleccionada &&
        cantidadSesiones &&
        (
          !archivoCargado ||
          fileIdDocumento
        )
    ),

    mostrarMensajeSinUnidades: Boolean(
      !loadingUnidades &&
        gradoSeleccionado &&
        unidades.length === 0
    ),

    isInitializing,
    loadingCursos,
    loadingGrados,
    loadingUnidades,

    niveles,
    areas,
    grados,
    unidades,

    nivelSeleccionado,
    areaSeleccionada,
    gradoSeleccionado,
    unidadSeleccionada,
    cantidadSesiones,

    tituloSesion,
    propositoSesion,

    /*
     * Se conserva este nombre para mantener
     * compatibilidad con SessionInputView.
     */
    documentoAnalizado: archivoCargado,

    archivoPersistido:
      archivoCargado
        ? infoArchivoDocumento
        : null,

    fileIdDocumento,

    handleArchivoSeleccionado,
    handleDocumentoAnalizado,
    handleArchivoEliminado,

    handleNivelChange,
    handleAreaChange,
    handleGradoChange,
    handleUnidadChange,
    handleCantidadSesionesChange,

    handleContinuar,
  };
};

export default useSessionInputStep;
